import { useState, useEffect } from "react";
import {
  Ship,
  X,
  Anchor,
  BadgeInfo,
  BookText,
  Building,
  Flag,
  Ruler,
  Sailboat,
  Weight,
  Hammer,
  Calendar,
  Radio,
  Hash,
  Hotel,
  ShieldCheck,
  Mail,
  Snowflake,
  Award,
  Fuel,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { createVessel, updateVessel } from "../../../services/vesselService";
import { getAllCompanies } from "../../../services/companyService";
import { fetchFleets } from "../../../services/fleetService";
import VesselClassDropdown from "./VesselClassDropdown";

const AddEditVesselModal = ({
  open,
  onClose,
  onSubmit,
  mode = "add",
  vessel = null,
  vessels,
}) => {
  const [form, setForm] = useState({
    name: "",
    vesselClass: "",
    vesselType: "",
    imoNumber: "",
    classification: "",
    vesselCode: "",
    callSign: "",
    portOfRegistry: "",
    flag: "",
    yearBuilt: "",
    builderInfo: "",
    dimensions: {
      loa: "",
      boa: "",
      depth: "",
      draft: "",
    },
    pitch: "",
    grossTonnage: "",
    netTonnage: "",
    deadweight: "",
    accommodation: "",
    company: "",
    fleet: null,
    email1: "",
    email2: "",
    iggType: "",

    // NEW FIELDS - Updated to string values for YES/NO, ON/OFF
    benchmarkYear: "",
    capacity: "",
    lpp: "",
    breadth: "",
    tDraft: "",
    totalCargoCapacity: "",
    dwtEnhanced: "",
    // reeferkwh: "",
    howManyYearsComplDeficient: "",
    rewardFactorWind: "",
    categoryEnergyEfficiency: "",
    sfocUsed: "",
    electricalEngineType: "",
    iceClassShip: "NO",
    iceClassType: "",
    defaultSFOCUsed: "NO",
    fuelEfficiencyTargets: {
      totalMTPerDay: "",
    },
  });

  const vesselTypeOptions = [
    "Container ships",
    "Bulk carrier ships",
    "Fishing vessels",
    "Dredgers",
    "High-speed craft",
    "Gas carriers",
    "Offshore ships",
    "Passenger ships",
    "Naval ships",
    "Livestock carriers",
    "Roll-on Roll-Off ships",
    // "Tanker ships",
    "Oil Tanker",
    "Chemical Tanker",
    "Shuttle Tanker",
    "Heavy life ships",
    "Yacht",
    "Tugs",
  ];

  const iceClassTypeOptions = [
    "IA Super",
    "IA",
    "IB",
    "IC",
    "II",
    "III",
    "Arc 4",
    "Arc 5",
    "Arc 6",
    "Arc 7",
    "Arc 8",
    "Arc 9",
  ];

  const electricalEngineTypeOptions = ["Two Stroke", "Four Stroke"];

  const [companies, setCompanies] = useState([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [fleets, setFleets] = useState([]);
  const [isLoadingFleets, setisLoadingFleets] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sfocDisabled, setSfocDisabled] = useState(false);

  const [selectedFuelTypes, setSelectedFuelTypes] = useState([]);
  const [mainEngines, setMainEngines] = useState([
    { name: "ME1", selected: false },
    { name: "ME2", selected: false },
  ]);
  const [auxiliaryEngines, setAuxiliaryEngines] = useState([
    { name: "AE1", selected: false },
    { name: "AE2", selected: false },
    { name: "AE3", selected: false },
    { name: "AE4", selected: false },
    { name: "AE5", selected: false },
    { name: "AE6", selected: false },
  ]);

  const handleToggle = (fieldName, valueType = "YESNO") => {
    console.log(fieldName);
    setForm((prev) => {
      const currentValue = prev[fieldName];
      let newValue;

      if (valueType === "ONOFF") {
        newValue = currentValue === "ON" ? "OFF" : "ON";
      } else {
        newValue = currentValue === "YES" ? "NO" : "YES";
      }
      console.log("newValue", newValue);
      if (fieldName === "defaultSFOCUsed" && newValue === "YES") {
        form.sfocUsed = "";
        setSfocDisabled(true);
      } else {
        setSfocDisabled(false);
      }
      return { ...prev, [fieldName]: newValue };
    });
  };

  useEffect(() => {
    const fetchCompanies = async () => {
      setIsLoadingCompanies(true);
      try {
        const response = await getAllCompanies();
        if (response.status === "success") {
          setCompanies(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch companies:", error);
      } finally {
        setIsLoadingCompanies(false);
      }
    };

    const getFleets = async () => {
      try {
        const res = await fetchFleets();
        if (res.data.status === "success") {
          setFleets(res.data.data);
        } else {
          toast.error("Failed to load fleets");
        }
      } catch (err) {
        toast.error("Error loading fleets");
      } finally {
        setisLoadingFleets(false);
      }
    };

    if (open) {
      fetchCompanies();
      getFleets();

      console.log("vessel", vessel);
      if (mode === "edit" && vessel) {
        if (vessel?.defaultSFOCUsed === "YES") {
          setSfocDisabled(true);
        } else {
          setSfocDisabled(false);
        }
        setForm({
          ...vessel,
          vesselType: vessel.vesselType || "",
          company: vessel.company._id || "",
          dimensions: {
            loa: vessel?.dimensions?.loa || "",
            boa: vessel?.dimensions?.boa || "",
            depth: vessel?.dimensions?.depth || "",
            draft: vessel?.dimensions?.draft || "",
          },
          // Initialize new fields with existing data
          benchmarkYear: vessel?.benchmarkYear || "",
          capacity: vessel?.capacity || "",
          lpp: vessel?.lpp || "",
          breadth: vessel?.breadth || "",
          tDraft: vessel?.tDraft || "",
          totalCargoCapacity: vessel?.totalCargoCapacity || "",
          dwtEnhanced: vessel?.dwtEnhanced || "",
          // reeferkwh: vessel?.reeferkwh || "",
          howManyYearsComplDeficient: vessel?.howManyYearsComplDeficient || "",
          rewardFactorWind: vessel?.rewardFactorWind || "",
          categoryEnergyEfficiency: vessel?.categoryEnergyEfficiency || "",
          iceClassShip: vessel?.iceClassShip || "NO",
          sfocUsed: vessel?.sfocUsed || "",
          iceClassType: vessel?.iceClassType || "",
          electricalEngineType: vessel?.electricalEngineType || "",
          defaultSFOCUsed: vessel?.defaultSFOCUsed || "NO",

          fuelEfficiencyTargets: vessel.fuelEfficiencyTargets || {
            totalMTPerDay: "",
          },
        });

        // Set fuel types and engines for edit mode
        if (vessel.fuelTypes) {
          setSelectedFuelTypes(vessel.fuelTypes);
        }

        if (vessel.mainEngines) {
          const updatedMainEngines = mainEngines.map((engine) => ({
            ...engine,
            selected: vessel.mainEngines.includes(engine.name),
          }));
          setMainEngines(updatedMainEngines);
        }

        if (vessel.auxiliaryEngines) {
          const updatedAuxEngines = auxiliaryEngines.map((engine) => ({
            ...engine,
            selected: vessel.auxiliaryEngines.includes(engine.name),
          }));
          setAuxiliaryEngines(updatedAuxEngines);
        }
      } else {
        setForm({
          name: "",
          vesselClass: "",
          vesselType: "",
          imoNumber: "",
          classification: "",
          // letterNumber: "",
          vesselCode: "",
          callSign: "",
          portOfRegistry: "",
          flag: "",
          yearBuilt: "",
          builderInfo: "",
          dimensions: { loa: "", boa: "", depth: "", draft: "" },
          pitch: "",
          grossTonnage: "",
          netTonnage: "",
          deadweight: "",
          accommodation: "",
          company: "",
          fleet: null,

          benchmarkYear: "",
          capacity: "",
          lpp: "",
          breadth: "",
          tDraft: "",
          totalCargoCapacity: "",
          dwtEnhanced: "",
          // reeferkwh: "",
          howManyYearsComplDeficient: "",
          rewardFactorWind: "",
          categoryEnergyEfficiency: "",
          sfocUsed: "",
          iceClassType: "",
          electricalEngineType: "",

          iceClassShip: "NO",
          defaultSFOCUsed: "NO",

          fuelEfficiencyTargets: {
            totalMTPerDay: "",
          },
        });

        setSelectedFuelTypes([]);
        setMainEngines(
          mainEngines.map((engine) => ({ ...engine, selected: false }))
        );
        setAuxiliaryEngines(
          auxiliaryEngines.map((engine) => ({ ...engine, selected: false }))
        );
      }
      setErrors({});
    }
  }, [open, mode, vessel]);

  const handleFuelTypeChange = (fuelType) => {
    setSelectedFuelTypes((prev) =>
      prev.includes(fuelType)
        ? prev.filter((type) => type !== fuelType)
        : [...prev, fuelType]
    );
  };

  const handleMainEngineChange = (index) => {
    const updatedEngines = [...mainEngines];
    updatedEngines[index].selected = !updatedEngines[index].selected;
    setMainEngines(updatedEngines);
  };

  const handleAuxiliaryEngineChange = (index) => {
    const updatedEngines = [...auxiliaryEngines];
    updatedEngines[index].selected = !updatedEngines[index].selected;
    setAuxiliaryEngines(updatedEngines);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name in form.dimensions) {
      setForm((prev) => ({
        ...prev,
        dimensions: {
          ...prev.dimensions,
          [name]: value,
        },
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateVesselCode = (
    vesselCode,
    vesselName,
    currentVesselId = null
  ) => {
    if (!vesselCode || !vesselCode.trim()) return "Vessel Code is required";

    if (!vesselName || !vesselName.trim()) {
      return "Vessel Name must be entered before Vessel Code";
    }

    // Enforce uppercase only
    if (vesselCode !== vesselCode.toUpperCase()) {
      return "Vessel Code must be in uppercase letters only";
    }

    const regex = /^[A-Z]{3}$/;
    if (!regex.test(vesselCode)) {
      const expected = getExpectedCode(vesselName);
      return `Vessel Code must be exactly 3 uppercase letters (e.g., ${expected})`;
    }

    // Check for duplicates, excluding the current vessel
    const exists = vessels.some(
      (v) =>
        v.vesselCode &&
        v.vesselCode.startsWith(vesselCode) &&
        v._id !== currentVesselId
    );

    if (exists) {
      return "Vessel Code already exists.";
    }

    return "";
  };

  function getExpectedCode(vesselName) {
    const parts = vesselName.trim().split(/\s+/);

    let code = "";

    for (const part of parts) {
      for (const char of part) {
        if (code.length < 3) {
          code += char.toUpperCase();
        }
      }
      if (code.length >= 3) break;
    }

    return code;
  }

  const validateForm = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Vessel name is required";
    if (!form.imoNumber.trim()) newErrors.imoNumber = "IMO number is required";
    if (form.imoNumber && !/^\d+$/.test(form.imoNumber))
      newErrors.imoNumber = "IMO number must be numeric";
    if (form.imoNumber && form.imoNumber.length !== 7)
      newErrors.imoNumber = "IMO number must be exactly 7 digits";
    if (!form.company) newErrors.company = "Company is required";
    if (form.benchmarkYear === "" || form.benchmarkYear == null) {
      newErrors.benchmarkYear = "Benchmark Year is required";
    }

    if (!form.email1) {
      newErrors.email1 = "Email 1 is required";
    } else if (form.email1 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email1)) {
      newErrors.email1 = "Invalid email format";
    }

    if (form.email2 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email2)) {
      newErrors.email2 = "Invalid email format";
    }

    if (form.defaultSFOCUsed === "YES" && form.electricalEngineType === "") {
      console.log(form.electricalEngineType);
      toast.error(
        "Default SFOC is enabled. Please select the electrical engine type."
      );
      newErrors.electricalEngineError =
        "Default SFOC is enabled. Please select the electrical engine type.";
    }

    if (form.defaultSFOCUsed === "NO" && form.sfocUsed === "") {
      toast.error(
        "Default SFOC is not enabled. Please enter the SFOC used value."
      );
      newErrors.sfocError =
        "Default SFOC is not enabled. Please enter the SFOC used value.";
    }

    const numericFields = [
      { name: "yearBuilt", label: "Year Built" },
      { name: "pitch", label: "Pitch"},
      { name: "grossTonnage", label: "Gross Tonnage" },
      { name: "netTonnage", label: "Net Tonnage" },
      { name: "deadweight", label: "Deadweight" },
      { name: "dimensions.loa", label: "LOA" },
      { name: "dimensions.boa", label: "BOA" },
      { name: "dimensions.depth", label: "Depth" },
      { name: "dimensions.draft", label: "Draft" },
      // NEW NUMERIC FIELDS
      { name: "benchmarkYear", label: "Benchmark Year" },
      { name: "capacity", label: "Capacity" },
      { name: "lpp", label: "LPP" },
      { name: "breadth", label: "B (Breadth)" },
      { name: "tDraft", label: "T (Draft)" },
      { name: "totalCargoCapacity", label: "Total Cargo Capacity" },
      { name: "dwtEnhanced", label: "DWT Enhanced" },
      // { name: "reeferkwh", label: "Reefer kWh" },
      {
        name: "howManyYearsComplDeficient",
        label: "Years Compliance Deficient",
      },
      { name: "rewardFactorWind", label: "Reward Factor Wind" },
      { name: "sfocUsed", label: "SFOC used" },
    ];

    numericFields.forEach(({ name, label }) => {
      const value = name.includes(".")
        ? form.dimensions[name.split(".")[1]]
        : form[name];

      if (value && !/^-?\d*\.?\d+$/.test(value)) {
        newErrors[name] = `${label} must be a valid number`;
      }

      if (value) {
        // Benchmark Year should be a reasonable year
        if (name === "benchmarkYear") {
          const year = parseInt(value);
          if (year < 1900 || year > new Date().getFullYear() + 5) {
            newErrors[name] = `${label} must be a valid year between 1900 and ${
              new Date().getFullYear() + 5
            }`;
          }
        }

        if (name !== "rewardFactorWind" && parseFloat(value) < 0) {
          newErrors[name] = `${label} must be a positive number`;
        }
      }
    });

    Object.entries(form.fuelEfficiencyTargets).forEach(([field, value]) => {
      if (value && !/^-?\d*\.?\d+$/.test(value)) {
        newErrors.fuelEfficiencyTargets = {
          ...newErrors.fuelEfficiencyTargets,
          [field]: "Must be a valid number",
        };
      }
    });

    const vesselCodeError = validateVesselCode(
      form.vesselCode || "",
      form.name || "",
      form._id
    );
    if (vesselCodeError) {
      newErrors.vesselCode = vesselCodeError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      // Scroll to the first error if validation fails
      const firstError = Object.keys(errors)[0];
      if (firstError) {
        const element = document.querySelector(`[name="${firstError}"]`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          element.focus();
        }
      }
      return;
    }

    const selectedMainEngines = mainEngines
      .filter((engine) => engine.selected)
      .map((engine) => engine.name);

    const selectedAuxEngines = auxiliaryEngines
      .filter((engine) => engine.selected)
      .map((engine) => engine.name);

    setIsSubmitting(true);
    const rawPayload = {
      ...form,
      fuelTypes: selectedFuelTypes,
      mainEngines: selectedMainEngines,
      auxiliaryEngines: selectedAuxEngines,
      yearBuilt: Number(form.yearBuilt),
      pitch: Number(form.pitch),
      grossTonnage: Number(form.grossTonnage),
      netTonnage: Number(form.netTonnage),
      deadweight: Number(form.deadweight),
      dimensions: {
        loa: Number(form.dimensions.loa),
        boa: Number(form.dimensions.boa),
        depth: Number(form.dimensions.depth),
        draft: Number(form.dimensions.draft),
      },

      benchmarkYear: Number(form.benchmarkYear) || 0,
      capacity: Number(form.capacity) || 0,
      lpp: Number(form.lpp) || 0,
      breadth: Number(form.breadth) || 0,
      tDraft: Number(form.tDraft) || 0,
      totalCargoCapacity: Number(form.totalCargoCapacity) || 0,
      dwtEnhanced: Number(form.dwtEnhanced) || 0,
      // reeferkwh: Number(form.reeferkwh) || 0,
      howManyYearsComplDeficient: Number(form.howManyYearsComplDeficient) || 0,
      rewardFactorWind: Number(form.rewardFactorWind) || 0,
      categoryEnergyEfficiency: form.categoryEnergyEfficiency,
      iceClassShip: form.iceClassShip,
      iceClassType: form.iceClassType,
      electricalEngineType: form.electricalEngineType,
      defaultSFOCUsed: form.defaultSFOCUsed,
      sfocUsed: Number(form.sfocUsed) || 0,

      fuelEfficiencyTargets: {
        totalMTPerDay: Number(form.fuelEfficiencyTargets.totalMTPerDay ?? 0),
      },
    };

    const { _id, createdAt, updatedAt, __v, ...cleanPayload } = rawPayload;
    console.log("cleanPayload:", cleanPayload);

    try {
      const res =
        mode === "edit"
          ? await updateVessel(vessel._id, cleanPayload)
          : await createVessel(cleanPayload);

      if (res.status === "success") {
        toast.success(
          `Vessel ${mode === "edit" ? "updated" : "added"} successfully!`
        );
        onSubmit();
        onClose();
      } else {
        toast.error(res.message || "Something went wrong.");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response.data.message);
      // toast.error("Failed to submit vessel.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  const buttonLabel = isSubmitting
    ? mode === "edit"
      ? "Updating..."
      : "Creating..."
    : mode === "edit"
    ? "Update Vessel"
    : "Add Vessel";

  const ToggleSwitch = ({
    fieldName,
    label,
    valueType = "YESNO",
    icon: Icon,
  }) => (
    <div className="flex items-center justify-between">
      <label className="font-medium flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4" />}
        {label}
      </label>
      <button
        type="button"
        onClick={() => handleToggle(fieldName, valueType)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#27ade3] focus:ring-offset-2 ${
          (
            valueType === "ONOFF"
              ? form[fieldName] === "ON"
              : form[fieldName] === "YES"
          )
            ? "bg-[#27ade3]"
            : "bg-gray-200"
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            (
              valueType === "ONOFF"
                ? form[fieldName] === "ON"
                : form[fieldName] === "YES"
            )
              ? "translate-x-6"
              : "translate-x-1"
          }`}
        />
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl flex flex-col max-h-[90vh] overflow-hidden">
        {/* FORM HEADER */}
        <div className="relative bg-white rounded-t-3xl border-b border-gray-200 px-6 pt-5 pb-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 border border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
              <Ship className="w-5 h-5 text-gray-600" />
            </div>
            <div className="flex flex-col">
              <h2 className="text-lg font-semibold text-gray-800 tracking-tight">
                {mode === "edit" ? "Edit Vessel" : "Add New Vessel"}
              </h2>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-100 transition"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* FORM CONTENT */}
        <div className="flex-1 px-6 py-5 overflow-y-auto grid grid-cols-2 gap-6 text-sm text-gray-700 bg-white">
          {[
            { label: "Vessel Name", name: "name", icon: Sailboat },
            {
              label: "Vessel Class",
              name: "vesselClass",
              icon: ShieldCheck,
              type: "dropdown",
            },
            { label: "Vessel Type", name: "vesselType", icon: Ship }, // Added Vessel Type
            { label: "IMO Number", name: "imoNumber", icon: Hash },
            {
              label: "Classification",
              name: "classification",
              icon: BadgeInfo,
            },
            // { label: "Letter Number", name: "letterNumber", icon: BookText },
            {
              label: "Vessel Code",
              name: "vesselCode",
              icon: BookText,
              sub_label: "(eg: VES)",
            },
            { label: "Call Sign", name: "callSign", icon: Radio },
            { label: "Port of Registry", name: "portOfRegistry", icon: Anchor },
            { label: "Flag", name: "flag", icon: Flag },
            {
              label: "Year Built",
              name: "yearBuilt",
              icon: Calendar,
            },
            { label: "Builder Info", name: "builderInfo", icon: Hammer },
            {
              label: "Pitch",
              name: "pitch",
              icon: Weight,
            },
            {
              label: "Gross Tonnage",
              name: "grossTonnage",
              icon: Weight,
            },
            {
              label: "Net Tonnage",
              name: "netTonnage",
              icon: Weight,
            },
            {
              label: "Deadweight",
              name: "deadweight",
              icon: Weight,
            },
            { label: "Accommodation", name: "accommodation", icon: Hotel },
            { label: "Email 1", name: "email1", type: "email", icon: Mail },
            { label: "Email 2", name: "email2", type: "email", icon: Mail },
          ].map(({ label, name, type, icon: Icon, sub_label }) => (
            <div key={name}>
              <label className="mb-1 block font-medium">
                {label}{" "}
                <span className="text-xs font-normal text-gray-500">
                  {sub_label}
                </span>
              </label>
              {name === "vesselClass" ? (
                // Use the custom dropdown for vessel class
                <VesselClassDropdown
                  value={form.vesselClass}
                  onChange={handleChange}
                  error={errors.vesselClass}
                />
              ) : name === "vesselType" ? (
                // Vessel Type Dropdown
                <div className="relative">
                  <select
                    name={name}
                    value={form[name]}
                    onChange={handleChange}
                    className={`w-full rounded-lg border px-4 py-2.5 pr-10 appearance-none bg-white focus:outline-none focus:ring-1 ${
                      errors[name]
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                    }`}
                  >
                    <option value="">Select Vessel Type</option>
                    {vesselTypeOptions.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  {Icon && (
                    <Icon className="absolute right-3 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
                  )}
                </div>
              ) : (
                // Regular Input Field
                <div className="relative">
                  <input
                    name={name}
                    type={type || "text"}
                    value={form[name]}
                    onChange={handleChange}
                    placeholder={label}
                    className={`w-full rounded-lg border px-4 py-2.5 pr-10 bg-white transition-all placeholder:text-gray-400 focus:outline-none focus:ring-1 ${
                      errors[name]
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                    }`}
                  />
                  {Icon && (
                    <Icon className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                  )}
                </div>
              )}
              {errors[name] && (
                <p className="text-red-500 text-xs mt-1">{errors[name]}</p>
              )}
            </div>
          ))}

          {/* Dimensions */}
          {["loa", "boa", "depth", "draft"].map((dim) => {
            const isBOA = dim === "boa";
            const labelText = isBOA
              ? "Breadth"
              : `Dimension - ${dim.toUpperCase()}`;
            const placeholderText = isBOA
              ? "Breadth of vessel"
              : `Enter ${dim.toUpperCase()} (m)`;

            return (
              <div key={dim}>
                <label className="mb-1 block font-medium">{labelText}</label>
                <div className="relative">
                  <input
                    name={dim}
                    value={form.dimensions[dim]}
                    onChange={handleChange}
                    placeholder={placeholderText}
                    className="w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1 border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                  />
                  <Ruler className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                </div>
              </div>
            );
          })}

          {/* Company Select */}
          <div>
            <label className="mb-1 block font-medium">Company</label>
            <div className="relative">
              <select
                name="company"
                value={form.company}
                onChange={handleChange}
                disabled={isLoadingCompanies}
                className={`w-full rounded-lg border px-4 py-2.5 pr-10 appearance-none bg-white focus:outline-none focus:ring-1 ${
                  errors.company
                    ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                    : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                } ${isLoadingCompanies ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <option value="">
                  {isLoadingCompanies
                    ? "Loading companies..."
                    : "Select a company"}
                </option>
                {companies.map((comp) => (
                  <option key={comp._id} value={comp._id}>
                    {comp.name}
                  </option>
                ))}
              </select>
              <Building className="absolute right-3 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
              {isLoadingCompanies && (
                <div className="absolute right-10 top-3">
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>
            {errors.company && (
              <p className="text-red-500 text-xs mt-1">{errors.company}</p>
            )}
          </div>

          {/* Fleet Select */}
          <div>
            <label className="mb-1 block font-medium">Fleet</label>
            <div className="relative">
              <select
                name="fleet"
                value={form.fleet}
                onChange={handleChange}
                className={`w-full rounded-lg border px-4 py-2.5 pr-10 appearance-none bg-white focus:outline-none focus:ring-1 ${
                  errors.fleet
                    ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                    : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                }`}
              >
                <option value="">Select a fleet</option>
                {fleets.map((fleet) => (
                  <option key={fleet.fleetId} value={fleet.fleetId}>
                    {fleet.fleetName}
                  </option>
                ))}
              </select>
              <Ship className="absolute right-3 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
            {errors.fleet && (
              <p className="text-red-500 text-xs mt-1">{errors.fleet}</p>
            )}
          </div>

          {/* Fuel Types Selection */}
          <div className="col-span-2 my-2">
            <label className="text-lg font-semibold text-gray-800 pb-2">
              Fuel Types
            </label>

            <div className="flex gap-2 mt-4">
              {["LFO", "HFO", "MGO"].map((fuelType) => (
                <div
                  key={fuelType}
                  onClick={() => handleFuelTypeChange(fuelType)}
                  className={`px-2 py-1 rounded-md border cursor-pointer transition-all text-sm font-medium ${
                    selectedFuelTypes.includes(fuelType)
                      ? "border-[#27ade3] bg-[#e6f4fc] text-[#1a325d]"
                      : "border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {fuelType}
                </div>
              ))}
            </div>
          </div>

          {/* Main Engines Selection */}
          <div className="col-span-2 my-2">
            <label className="text-lg font-semibold text-gray-800 pb-4">
              Main Engines
            </label>
            <div className="flex gap-2 mt-4">
              {mainEngines.map((engine, index) => (
                <div
                  key={engine.name}
                  onClick={() => handleMainEngineChange(index)}
                  className={`px-2 py-1 rounded-md border cursor-pointer transition-all text-sm font-medium ${
                    engine.selected
                      ? "border-[#27ade3] bg-[#e6f4fc] text-[#1a325d]"
                      : "border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {engine.name}
                </div>
              ))}
            </div>
          </div>

          {/* Auxiliary Engines Selection */}
          <div className="col-span-2 my-2">
            <label className="text-lg font-semibold text-gray-800 pb-4">
              Auxiliary Engines
            </label>
            <div className="flex gap-2 mt-4">
              {auxiliaryEngines.map((engine, index) => (
                <div
                  key={engine.name}
                  onClick={() => handleAuxiliaryEngineChange(index)}
                  className={`px-2 py-1 rounded-md border cursor-pointer transition-all text-sm font-medium ${
                    engine.selected
                      ? "border-[#27ade3] bg-[#e6f4fc] text-[#1a325d]"
                      : "border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {engine.name}
                </div>
              ))}
            </div>
          </div>

          <div className="col-span-2 my-2">
            <label
              htmlFor="iggType"
              className="text-lg font-semibold text-gray-800 pb-2 block"
            >
              IGG Type
            </label>
            <div className="relative">
              <select
                id="iggType"
                name="iggType"
                value={form.iggType || ""}
                onChange={(e) => setForm({ ...form, iggType: e.target.value })}
                className={`w-full rounded-lg border px-4 py-2.5 appearance-none placeholder:text-gray-400 focus:outline-none focus:ring-1 transition-all ${
                  errors.iggType
                    ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                    : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                } `}
                required
              >
                <option value=""> Select IGG Type </option>
                <option value="MPG-400">MPG-400</option>
                <option value="MPG-700">MPG-700</option>
                <option value="MPG-800">MPG-800</option>
                <option value="MPG-900">MPG-900</option>
                <option value="MPG-1000">MPG-1000</option>
                <option value="MPG-1100">MPG-1100</option>
                <option value="MPG-1300">MPG-1300</option>
                <option value="MPG-1600">MPG-1600</option>
                <option value="MPG-1600L">MPG-1600L</option>
              </select>

              <svg
                className="w-4 h-4 absolute right-3 top-3 pointer-events-none text-gray-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>

            {errors.iggType && (
              <p className="text-red-500 text-xs mt-1">{errors.iggType}</p>
            )}

            {mode === "edit" && (
              <input type="hidden" name="iggType" value={form.iggType || ""} />
            )}
          </div>

          {/* Fuel Efficiency Targets */}
          <div className="col-span-2 mt-4 mb-2">
            <h3 className="text-lg font-semibold text-gray-800 pb-2">
              Fuel Efficiency Targets
            </h3>
          </div>

          {[{ label: "Total MT/Day", name: "totalMTPerDay", unit: "MT" }].map(
            ({ label, name, unit }) => (
              <div key={name}>
                <label className="mb-1 block font-medium">{label}</label>
                <div className="relative">
                  <input
                    type="text"
                    name={name}
                    value={form.fuelEfficiencyTargets[name]}
                    onChange={(e) => {
                      if (
                        e.target.value === "" ||
                        /^[0-9]*\.?[0-9]*$/.test(e.target.value)
                      ) {
                        setForm((prev) => ({
                          ...prev,
                          fuelEfficiencyTargets: {
                            ...prev.fuelEfficiencyTargets,
                            [name]: e.target.value,
                          },
                        }));
                      }
                    }}
                    placeholder={`Enter ${label}`}
                    className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1 ${
                      errors.fuelEfficiencyTargets?.[name]
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                    }`}
                  />
                  {unit && (
                    <span className="absolute right-3 top-3 text-xs text-gray-500">
                      {unit}
                    </span>
                  )}
                </div>
                {errors.fuelEfficiencyTargets?.[name] && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.fuelEfficiencyTargets[name]}
                  </p>
                )}
              </div>
            )
          )}

          <div className="col-span-2 border-t pt-4 mt-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Award className="w-5 h-5" />
              Additional Vessel Specifications
            </h3>
          </div>

          {/* Benchmark Year */}
          <div>
            <label className="mb-1 block font-medium">Benchmark Year</label>
            <div className="relative">
              <input
                name="benchmarkYear"
                type="text"
                value={form.benchmarkYear}
                onChange={handleChange}
                placeholder="e.g., 2020"
                className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1 ${
                  errors.benchmarkYear
                    ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                    : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                }`}
              />
              <Calendar className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
            </div>
            {errors.benchmarkYear && (
              <p className="text-red-500 text-xs mt-1">
                {errors.benchmarkYear}
              </p>
            )}
          </div>

          {/* Cargo and DWT */}
          <div>
            <label className="mb-1 block font-medium">
              Total Cargo Capacity
            </label>
            <input
              name="totalCargoCapacity"
              type="text"
              value={form.totalCargoCapacity}
              onChange={handleChange}
              placeholder="Total cargo capacity"
              className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1 ${
                errors.totalCargoCapacity
                  ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                  : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
              }`}
            />
            {errors.totalCargoCapacity && (
              <p className="text-red-500 text-xs mt-1">
                {errors.totalCargoCapacity}
              </p>
            )}
          </div>

          <div>
            <label className="mb-1 block font-medium">DWT Enhanced</label>
            <input
              name="dwtEnhanced"
              type="text"
              value={form.dwtEnhanced}
              onChange={handleChange}
              placeholder="Enhanced deadweight"
              className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1 ${
                errors.dwtEnhanced
                  ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                  : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
              }`}
            />
            {errors.dwtEnhanced && (
              <p className="text-red-500 text-xs mt-1">{errors.dwtEnhanced}</p>
            )}
          </div>

          {/* Reefer and Compliance */}
          {/* <div>
            <label className="mb-1 block font-medium">Reefer kWh</label>
            <input
              name="reeferkwh"
              type="text"
              value={form.reeferkwh}
              onChange={handleChange}
              placeholder="Reefer energy consumption"
              className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1 ${
                errors.reeferkwh
                  ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                  : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
              }`}
            />
            {errors.reeferkwh && (
              <p className="text-red-500 text-xs mt-1">{errors.reeferkwh}</p>
            )}
          </div> */}

          <div>
            <label className="mb-1 block font-medium">
              Years Compliance Deficient
            </label>
            <input
              name="howManyYearsComplDeficient"
              type="text"
              value={form.howManyYearsComplDeficient}
              onChange={handleChange}
              placeholder="Number of years"
              className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1 ${
                errors.howManyYearsComplDeficient
                  ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                  : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
              }`}
            />
            {errors.howManyYearsComplDeficient && (
              <p className="text-red-500 text-xs mt-1">
                {errors.howManyYearsComplDeficient}
              </p>
            )}
          </div>

          {/* Reward Factor Wind */}
          <div>
            <label className="mb-1 block font-medium flex items-center gap-2">
              Reward Factor Wind
            </label>
            <input
              name="rewardFactorWind"
              type="text"
              value={form.rewardFactorWind}
              onChange={handleChange}
              placeholder="Wind reward factor"
              className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1 ${
                errors.rewardFactorWind
                  ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                  : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
              }`}
            />
            {errors.rewardFactorWind && (
              <p className="text-red-500 text-xs mt-1">
                {errors.rewardFactorWind}
              </p>
            )}
          </div>

          <div>
            <label className="mb-1 block font-medium flex items-center gap-2">
              Category for innovative energy efficiency
            </label>
            <input
              name="categoryEnergyEfficiency"
              type="text"
              value={form.categoryEnergyEfficiency}
              onChange={handleChange}
              placeholder="Category for innovative energy efficiency"
              className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1 ${
                errors.categoryEnergyEfficiency
                  ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                  : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
              }`}
            />
            {errors.categoryEnergyEfficiency && (
              <p className="text-red-500 text-xs mt-1">
                {errors.categoryEnergyEfficiency}
              </p>
            )}
          </div>

          {/* SFOC used value */}
          <div>
            <label className="mb-1 block font-medium flex items-center gap-2">
              SFOC used
              <span className="text-xs font-normal text-gray-500">
                (Must be given if Default SFOC Used is not enabled)
              </span>
            </label>
            <input
              name="sfocUsed"
              type="text"
              value={form.sfocUsed}
              onChange={handleChange}
              placeholder="SFOC value"
              className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1`}
              disabled={sfocDisabled}
            />
            {/* {errors.sfocError && (
              <p className="text-red-500 text-xs mt-1">
                {errors.sfocError}
              </p>
            )} */}
          </div>

          {/* Toggle switches in a grid */}
          <div className="col-span-2 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <ToggleSwitch
                  fieldName="defaultSFOCUsed"
                  label="Default SFOC Used"
                  valueType="YESNO"
                  icon={Fuel}
                />
              </div>

              {/* Ice Class Ship Toggle */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <ToggleSwitch
                  fieldName="iceClassShip"
                  label="Ice Class Ship"
                  valueType="YESNO"
                  icon={Snowflake}
                />
              </div>
            </div>
            <div className="flex w-full gap-6">
              {/* Electrical Engine Type */}
              {form.defaultSFOCUsed === "YES" && (
                <div className="w-full">
                  <label className="mb-1 block font-medium">
                    Electrical Engine Type
                  </label>
                  <select
                    name="electricalEngineType"
                    value={form.electricalEngineType}
                    onChange={handleChange}
                    className="w-full rounded-lg border px-4 py-2.5 bg-white focus:outline-none focus:ring-1 border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                  >
                    <option value="">Select Engine Type</option>
                    {electricalEngineTypeOptions.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Ice Class Type */}
              {form.iceClassShip === "YES" && (
                <div className="w-full">
                  <label className="mb-1 block font-medium">
                    Ice Class Type
                  </label>
                  <select
                    name="iceClassType"
                    value={form.iceClassType}
                    onChange={handleChange}
                    className="w-full rounded-lg border px-4 py-2.5 bg-white focus:outline-none focus:ring-1 border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                  >
                    <option value="">Select Ice Class</option>
                    {iceClassTypeOptions.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* FORM FOOTER */}
        <div className="bg-white rounded-b-3xl border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
          {/* Cancel Button */}
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>

          {/* Submit Button */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`px-5 py-2.5 text-sm rounded-lg text-white flex items-center gap-2 transition-colors font-medium ${
              isSubmitting
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-[#1a325d] hover:bg-[#21426e]"
            }`}
          >
            {isSubmitting && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            <span>{buttonLabel}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEditVesselModal;
